/**
 * usePrefersReducedMotion — reactive `prefers-reduced-motion` check (FLO-520).
 *
 * Same shape as {@link ./useIsMobile}: a synchronous one-shot read usable during
 * render, plus a hook that stays live if the visitor flips the OS setting.
 *
 * Most of our motion is disabled in CSS (`@media (prefers-reduced-motion:
 * reduce)`), which is the right tool — reach for this hook only when the
 * preference has to drive a DECISION that CSS cannot express. It has exactly one
 * caller-shaped job today: the launcher's idle-loop video must not be given
 * `autoplay` at all (a CSS rule can't un-autoplay a `<video>`), and with no
 * poster to fall back to the launcher degrades to the pill entirely.
 */

import { useEffect, useState } from 'react';

/** The OS/browser "reduce motion" preference. */
export const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Synchronous, one-shot read. Returns `false` with no `window`/`matchMedia`
 * (SSR or the node test env) — motion is the default everywhere else in the
 * overlay, so "unknown" must not silently strip animation from every visitor.
 */
export function matchesReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;
}

/** Returns `true` while the visitor asks for reduced motion, live on change. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(matchesReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    const onChange = () => setReduced(mql.matches);
    // Re-sync once on mount: the preference may have changed between the
    // initial render and this effect running.
    onChange();
    // `addEventListener` is the modern API; `addListener` is the deprecated
    // fallback for older Safari (< 14) where the former is missing.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
}
